import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, Cell, ZAxis,
} from 'recharts';
import api from '../../services/api';
import './PlusFundApp.css';

import { C, SERIES_COLOUR, AC_COLOURS, MONTHS, isNum, pct, num, money, Dash, V, signCls, tiltCls, useFetch, Status, signStack, rangeVal } from './helpers';
import { MtdTiles, CurrencyStrip, ValuationTab, TicketTab, FlowCalcTab, HistoryTab, ContributionTab, FlowsTab } from './PlusFundPhase2';
import ReportTab from './PlusFundReport';
import { TradeLogTab } from './PlusFundOrders';

// ---- Front page -----------------------------------------------------------
function FrontPage({ summary, trailing, trailingError, valuation, currencyExposure }) {
  const tr = useMemo(() => {
    const m = {};
    (trailing?.rows || []).forEach((r) => { m[r.security_id] = r; });
    return m;
  }, [trailing]);
  if (!summary) return null;
  const { stats, limits, groups } = summary;
  const limit = (k) => limits.find((l) => l.key === k);
  const tile = (label, value, sub, breach) => (
    <div className={`pf-tile${breach ? ' pf-tile-breach' : ''}`}>
      <div className="pf-tile-label">{label}</div>
      <div className="pf-tile-value">{pct(value, 1) ?? '–'}</div>
      {sub && <div className="pf-tile-sub">{sub}</div>}
    </div>
  );
  // The front page is the client-safe view: no DDP tilts and no SN mirror account.
  // Those live on the Exposure vs DDP, Rebalancing and drift, and SN portfolio tabs.
  const allSecs = groups.flatMap((g) => g.securities);
  const totalWeight = allSecs.reduce((a, s) => a + (s.Weight_Fund || 0), 0);

  return (
    <>
      <div className="pf-tiles">
        {tile('Growth (net)', stats.Growth_Net, null, limit('growth_gross')?.ok === false)}
        {tile('Defence (net)', stats.Defensive_Net, 'ex cash', limit('defensive_gross')?.ok === false)}
        {tile('Net exposure', stats.Fund_Net)}
        {tile('Gross exposure', stats.Fund_Gross, `limit ${pct(limit('fund_gross')?.limit / 1, 0) ?? ''} of NAV`, limit('fund_gross')?.ok === false)}
        {tile('Non-AUD', stats.Non_AUD)}
        <MtdTiles valuation={valuation} />
        <div className="pf-tile">
          <div className="pf-tile-label">Fund NAV</div>
          <div className="pf-tile-value">{money(stats.NAV_Fund) ?? '–'}</div>
          <div className="pf-tile-sub">IB, as at {summary.as_at}</div>
        </div>
      </div>
      <div className="pf-toolbar">
        {limits.map((l) => (
          <span key={l.key} className={`pf-pill ${l.ok === false ? 'pf-pill-breach' : l.ok ? 'pf-pill-ok' : 'pf-pill-navy'}`}
                title={`${l.label}: ${pct(l.value, 2) ?? '–'} vs limit ${pct(l.limit, 0)}`}>
            {l.label} {l.ok === false ? 'BREACH' : 'OK'} · {pct(l.value, 1) ?? '–'} / {pct(l.limit, 0)}
          </span>
        ))}
        <span className="pf-pill pf-pill-navy">Hedging (options notional) {pct(summary.hedging_notional_weight, 1) ?? '–'}</span>
      </div>
      <CurrencyStrip data={currencyExposure} />
      {trailingError && <div className="pf-note">Trailing returns unavailable: {String(trailingError)}</div>}

      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead>
            <tr>
              <th className="pf-l">Symbol</th>
              <th className="pf-l">Security</th>
              <th>Weight</th>
              <th>Notional</th>
              <th className="pf-l">Ccy</th>
              <th className="pf-l">FX exp</th>
              <th title="Price-based, Datastream end of day">MTD</th>
              <th>1m</th>
              <th>3m</th>
              <th>12m</th>
              <th>Price</th>
              <th className="pf-l">Price date</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.asset_class}>
                <tr className="pf-group">
                  <td className="pf-l" colSpan={2}>{g.asset_class}</td>
                  <td className="pf-r"><V v={g.weight} /></td>
                  <td className="pf-r"><V v={g.notional_weight} /></td>
                  <td colSpan={8} />
                </tr>
                {g.securities.length === 0 && (
                  <tr><td className="pf-l pf-empty" colSpan={12}>No holdings</td></tr>
                )}
                {g.securities.map((s, i) => {
                  const t = tr[s.SecurityID];
                  const noSeries = t && t.reason;
                  const cell = (v) => (t && !noSeries ? <V v={v} signed /> : <span className="pf-dash" title={noSeries || 'no price series'}>–</span>);
                  return (
                    <tr key={s.SecurityID + i}>
                      <td className="pf-l"><strong>{s.Symbol}</strong></td>
                      <td className="pf-l">{s.SecurityDescription}<span className="pf-sub" style={{ marginLeft: 6 }}>{s.Sub_Asset_Class}{s.Source ? ' · ' + s.Source : ''}</span></td>
                      <td className="pf-r"><V v={s.Weight_Fund} /></td>
                      <td className="pf-r"><V v={s.NotionalWeight_Fund} /></td>
                      <td className="pf-l">{s.Currency}</td>
                      <td className="pf-l">{s.FX_EXP}</td>
                      <td className={`pf-r ${t && !noSeries ? signCls(t.mtd) : ''}`}>{cell(t?.mtd)}</td>
                      <td className={`pf-r ${t && !noSeries ? signCls(t.r1m) : ''}`}>{cell(t?.r1m)}</td>
                      <td className={`pf-r ${t && !noSeries ? signCls(t.r3m) : ''}`}>{cell(t?.r3m)}</td>
                      <td className={`pf-r ${t && !noSeries ? signCls(t.r12m) : ''}`}>{cell(t?.r12m)}</td>
                      <td className="pf-r">{t && !noSeries && isNum(t.price) ? num(t.price, 2) : <span className="pf-dash" title={noSeries || ''}>–</span>}</td>
                      <td className="pf-l pf-sub">{t?.price_date || ''}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            <tr className="pf-total">
              <td className="pf-l" colSpan={2}>Total</td>
              <td className="pf-r"><V v={totalWeight} /></td>
              <td className="pf-r"><V v={stats.Fund_Net} /></td>
              <td colSpan={8} />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="pf-foot">
        Weights are IB end-of-day fractions of fund NAV as at {summary.as_at}. MTD/1m/3m/12m are Datastream total-return index ratios (RI) to the latest available close; the
        price column is the latest close. A dash means no price series is mapped for that security (set ds_ticker on the APL tab).
      </div>
    </>
  );
}

// ---- Exposure vs DDP ------------------------------------------------------
function ExposureTab({ data }) {
  if (!data) return null;
  const rows = data.asset_classes;
  const chart = rows.filter((r) => isNum(r.Tilt)).map((r) => ({ name: r.Asset_Class, tilt: r.Tilt }));
  return (
    <>
      <div className="pf-grid-2">
        <div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Asset class</th><th>Fund</th><th>DDP</th><th>Plus target (2x)</th><th>Tilt</th><th className="pf-l">G / D</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.Asset_Class}>
                    <td className="pf-l"><strong>{r.Asset_Class === 'FX' ? 'Currency (non-AUD)' : r.Asset_Class}</strong></td>
                    <td className="pf-r"><V v={r.Weight_Fund / 100} /></td>
                    <td className="pf-r"><V v={r.DDP_Weight / 100} /></td>
                    <td className="pf-r"><V v={r.Plus_Target_Weight / 100} /></td>
                    <td className={`pf-r ${tiltCls(r.Tilt / 100, 0.005)}`}><V v={r.Tilt / 100} signed /></td>
                    <td className="pf-l pf-sub">{r.Growth_Defensive || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pf-section">
            <h2>Growth / defensive</h2>
            <div className="pf-table-scroll">
              <table className="pf-table">
                <thead><tr><th className="pf-l">Bucket</th><th>Fund</th><th>DDP</th><th>Plus target (2x)</th><th>Tilt</th></tr></thead>
                <tbody>
                  {data.growth_defensive.map((r) => (
                    <tr key={r.Growth_Defensive}>
                      <td className="pf-l"><strong>{r.Growth_Defensive}</strong></td>
                      <td className="pf-r"><V v={r.Weight_Fund / 100} /></td>
                      <td className="pf-r"><V v={r.DDP_Weight / 100} /></td>
                      <td className="pf-r"><V v={r.Plus_Target_Weight / 100} /></td>
                      <td className={`pf-r ${tiltCls(r.Tilt / 100, 0.005)}`}><V v={r.Tilt / 100} signed /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pf-foot">Defensive here includes cash (negative when the fund is geared), unlike the front page's Defence (net) tile which excludes it.</div>
          </div>
        </div>
        <div className="pf-chart">
          <h3>Tilt vs Plus target (percentage points of NAV)</h3>
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={chart} layout="vertical" margin={{ left: 30, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid stroke="#eef1f4" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => v.toFixed(0)} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11.5 }} />
              <Tooltip formatter={(v) => [v.toFixed(2) + ' pp', 'tilt']} />
              <ReferenceLine x={0} stroke={C.navy} />
              <Bar dataKey="tilt">
                {chart.map((d) => <Cell key={d.name} fill={d.tilt >= 0 ? C.tealDark : C.mauve} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="pf-foot">As at {data.as_at}. DDP weights are the Dynamic portfolio's latest Shadow_Data targets (DRU005DYN); the Plus target is 2x. Currency row: fund non-AUD exposure vs the DDP unhedged weight.</div>
    </>
  );
}

// ---- Rebalancing ----------------------------------------------------------
function RebalTab({ data }) {
  if (!data) return null;
  return (
    <>
      <div className="pf-tiles">
        <div className="pf-tile"><div className="pf-tile-label">NAV Fund</div><div className="pf-tile-value">{money(data.nav.NAV_Fund)}</div></div>
        <div className="pf-tile"><div className="pf-tile-label">NAV SN</div><div className="pf-tile-value">{money(data.nav.NAV_SN)}</div><div className="pf-tile-sub">SN adjustment {isNum(data.adjustment.SN) ? data.adjustment.SN.toFixed(4) : '–'}</div></div>
        <div className="pf-tile"><div className="pf-tile-label">NAV DK</div><div className="pf-tile-value">{money(data.nav.NAV_DK)}</div><div className="pf-tile-sub">DK adjustment {isNum(data.adjustment.DK) ? data.adjustment.DK.toFixed(4) : '–'}</div></div>
      </div>
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead>
            <tr>
              <th className="pf-l">Security</th><th className="pf-l">ID</th>
              <th>Wt Fund</th><th>Wt SN</th><th>Wt DK</th>
              <th>Qty Fund</th><th>Qty SN</th><th>Qty DK</th>
              <th title="Units to trade to bring the fund to the average of SN and DK weights">Rebal Fund</th>
              <th title="Units to trade in SN to match the fund weight">Rebal SN</th><th>Rebal DK</th>
              <th>IB price</th><th>FX</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={r.SecurityID + i}>
                <td className="pf-l"><strong>{r.SecurityDescription}</strong> <span className="pf-sub">{r.Symbol !== r.SecurityDescription ? r.Symbol : ''}</span></td>
                <td className="pf-l pf-code">{r.SecurityID}</td>
                <td className="pf-r"><V v={r.Weight_Fund} dp={2} /></td>
                <td className="pf-r"><V v={r.Weight_SN} dp={2} /></td>
                <td className="pf-r"><V v={r.Weight_DK} dp={2} /></td>
                <td className="pf-r"><V v={r.Quantity_Fund} kind="num" /></td>
                <td className="pf-r"><V v={r.Quantity_SN} kind="num" /></td>
                <td className="pf-r"><V v={r.Quantity_DK} kind="num" /></td>
                <td className={`pf-r ${signCls(r.Rebal_Fund)}`}><V v={r.Rebal_Fund} kind="num" /></td>
                <td className={`pf-r ${signCls(r.Rebal_SN)}`}><V v={r.Rebal_SN} kind="num" /></td>
                <td className={`pf-r ${signCls(r.Rebal_DK)}`}><V v={r.Rebal_DK} kind="num" /></td>
                <td className="pf-r"><V v={r.IB_Price} kind="num" dp={2} /></td>
                <td className="pf-r"><V v={r.FxRateToBase} kind="num" dp={4} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pf-foot">As at {data.as_at}. Rebal quantities are the Download_IB_Data.py static drift calculation (weight gap x NAV / IB price, cash in local currency).</div>
    </>
  );
}

// ---- SN portfolio ---------------------------------------------------------
// The SN account on its own, in dollars, for reporting: no fund comparators here
// (those stay on Rebalancing and drift). Value_SN is IB's MarketValueInBase (A$)
// and sums to NAV_SN; IB price is Value / Quantity, so it is already in A$ for
// USD lines.
const aud = (v, dp = 0) => (isNum(v) ? 'A$' + v.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : null);

function SNTab({ data }) {
  if (!data) return null;
  const rows = data.groups.flatMap((g) => g.securities);
  const downloadCsv = () => {
    const head = ['Asset class', 'Symbol', 'Security', 'Type', 'Currency', 'Quantity', 'IB price (A$)', 'Value (A$)', 'Weight'];
    const esc = (v) => (v === null || v === undefined ? '' : /[",\n]/.test(String(v)) ? '"' + String(v).replace(/"/g, '""') + '"' : String(v));
    const lines = [head.join(',')];
    data.groups.forEach((g) => g.securities.forEach((s) => lines.push([
      g.asset_class, s.Symbol, s.SecurityDescription, s.AssetType, s.Currency, s.Quantity_SN, s.IB_Price, s.Value_SN,
      s.Weight_SN].map(esc).join(','))));
    lines.push(['Total', '', '', '', '', '', '', data.total_value, rows.reduce((a, s) => a + (s.Weight_SN || 0), 0)].map(esc).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `SN_holdings_${data.as_at}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <>
      <div className="pf-tiles">
        <div className="pf-tile"><div className="pf-tile-label">NAV SN</div><div className="pf-tile-value">{aud(data.nav) ?? '–'}</div><div className="pf-tile-sub">IB account {data.account}, as at {data.as_at}</div></div>
        <div className="pf-tile"><div className="pf-tile-label">Invested</div><div className="pf-tile-value">{aud(data.invested) ?? '–'}</div><div className="pf-tile-sub">ex cash</div></div>
        <div className="pf-tile"><div className="pf-tile-label">Cash</div><div className="pf-tile-value">{aud(data.cash) ?? '–'}</div><div className="pf-tile-sub">all currencies, A$ (negative = borrowed)</div></div>
        <div className="pf-tile"><div className="pf-tile-label">Non-AUD</div><div className="pf-tile-value">{pct(data.non_aud, 1) ?? '–'}</div></div>
      </div>
      <div className="pf-toolbar">
        <button className="pf-btn" onClick={downloadCsv}>Download CSV</button>
      </div>
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead>
            <tr>
              <th className="pf-l">Symbol</th>
              <th className="pf-l">Security</th>
              <th className="pf-l">Ccy</th>
              <th>Quantity</th>
              <th title="Value / quantity, IB base currency">IB price (A$)</th>
              <th title="IB MarketValueInBase">Value (A$)</th>
              <th>Weight</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => (
              <React.Fragment key={g.asset_class}>
                <tr className="pf-group">
                  <td className="pf-l" colSpan={5}>{g.asset_class}</td>
                  <td className="pf-r">{aud(g.value)}</td>
                  <td className="pf-r"><V v={g.weight_sn} /></td>
                </tr>
                {g.securities.map((s, i) => (
                  <tr key={s.SecurityID + i}>
                    <td className="pf-l"><strong>{s.Symbol}</strong></td>
                    <td className="pf-l">{s.SecurityDescription}<span className="pf-sub" style={{ marginLeft: 6 }}>{s.Sub_Asset_Class || ''}</span></td>
                    <td className="pf-l">{s.Currency}</td>
                    <td className="pf-r"><V v={s.Quantity_SN} kind="num" /></td>
                    <td className="pf-r"><V v={s.IB_Price} kind="num" dp={2} /></td>
                    <td className="pf-r">{aud(s.Value_SN) ?? <Dash />}</td>
                    <td className="pf-r"><V v={s.Weight_SN} dp={2} /></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="pf-total">
              <td className="pf-l" colSpan={5}>Total</td>
              <td className="pf-r">{aud(data.total_value)}</td>
              <td className="pf-r"><V v={rows.reduce((a, s) => a + (s.Weight_SN || 0), 0)} /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="pf-foot">
        As at {data.as_at}, IB end of day. Values are IB MarketValueInBase (A$) and sum to NAV; cash lines include borrowed
        currency. Weights are fractions of NAV.
      </div>
    </>
  );
}

// ---- Performance ----------------------------------------------------------
function PerformanceTab({ perf, alloc }) {
  const cum = useMemo(() => (perf?.cumulative || []).map((r) => ({ ...r, date: r.date.slice(0, 7) })), [perf]);
  const dd = useMemo(() => (perf?.drawdowns || []).map((r) => ({ ...r, date: r.date.slice(0, 7) })), [perf]);
  const scatter = useMemo(() => {
    if (!perf) return [];
    const ret = perf.stats.find((s) => s.label === 'Annual Return') || {};
    const vol = perf.stats.find((s) => s.label === 'Volatility') || {};
    // Fund and peer group only (the asset-class proxies crowd the picture).
    return ['Dynamic Plus Fund', 'FE AMI Flexible Peers'].filter((s) => perf.series.includes(s))
      .map((s) => ({ name: s, x: (vol[s] || 0) * 100, y: (ret[s] || 0) * 100 }));
  }, [perf]);
  const allocRows = useMemo(() => signStack((alloc?.rows || []).map((r) => {
    const o = { date: r.date.slice(0, 7) };
    alloc.asset_classes.forEach((c) => { o[c] = isNum(r[c]) ? r[c] * 100 : 0; });
    return o;
  }), alloc?.asset_classes || []), [alloc]);
  if (!perf) return null;
  const pctTick = (v) => (v * 100).toFixed(0) + '%';
  const pctTip = (v) => (isNum(v) ? (v * 100).toFixed(2) + '%' : '–');
  return (
    <>
      <div className="pf-section"><h2>Monthly returns: Dynamic Plus Fund</h2></div>
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Year</th>{MONTHS.map((m) => <th key={m}>{m}</th>)}<th>YTD</th></tr></thead>
          <tbody>
            {perf.grid.map((g) => (
              <tr key={g.year}>
                <td className="pf-l"><strong>{g.year}</strong></td>
                {MONTHS.map((m, i) => <td key={m} className={`pf-r ${signCls(g.months[i + 1])}`}><V v={g.months[i + 1]} dp={2} /></td>)}
                <td className={`pf-r ${signCls(g.ytd)}`}><strong><V v={g.ytd} dp={2} /></strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pf-grid-2" style={{ marginTop: 16 }}>
        <div>
          <div className="pf-section"><h2>Period returns</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Period</th>{perf.series.map((s) => <th key={s}>{s}</th>)}</tr></thead>
              <tbody>
                {perf.periods.map((p) => (
                  <tr key={p.label}><td className="pf-l"><strong>{p.label}</strong></td>
                    {perf.series.map((s) => <td key={s} className={`pf-r ${signCls(p[s])}`}><V v={p[s]} dp={2} /></td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pf-foot">Periods over one year are annualised. S.I. = since inception (Feb 2021).</div>
        </div>
        <div>
          <div className="pf-section"><h2>Risk statistics</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Statistic</th>{perf.series.map((s) => <th key={s}>{s}</th>)}</tr></thead>
              <tbody>
                {perf.stats.map((p) => (
                  <tr key={p.label}><td className="pf-l"><strong>{p.label}</strong></td>
                    {perf.series.map((s) => <td key={s} className="pf-r">{p.label === 'Sharpe' ? (isNum(p[s]) ? p[s].toFixed(2) : '–') : <V v={p[s]} dp={2} />}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="pf-grid-2" style={{ marginTop: 16 }}>
        <div className="pf-chart">
          <h3>Cumulative return since inception</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cum} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
              <YAxis tickFormatter={pctTick} tick={{ fontSize: 11 }} width={50} />
              <Tooltip formatter={pctTip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {['Dynamic Plus Fund', 'Objective', 'FE AMI Flexible Peers'].filter((s) => perf.series.includes(s)).map((s) => (
                <Line key={s} type="linear" dataKey={s} stroke={SERIES_COLOUR[s]} dot={false} strokeWidth={s === 'Dynamic Plus Fund' ? 2.5 : 1.5} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="pf-chart">
          <h3>Drawdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dd} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
              <YAxis tickFormatter={pctTick} tick={{ fontSize: 11 }} width={50} />
              <Tooltip formatter={pctTip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {['Dynamic Plus Fund', 'FE AMI Flexible Peers'].filter((s) => perf.series.includes(s)).map((s) => (
                <Line key={s} type="linear" dataKey={s} stroke={SERIES_COLOUR[s]} dot={false} strokeWidth={s === 'Dynamic Plus Fund' ? 2.5 : 1.5} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="pf-grid-2" style={{ marginTop: 16 }}>
        <div className="pf-chart">
          <h3>Risk / reward (annualised, since inception)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ left: 0, right: 20, top: 10, bottom: 10 }}>
              <CartesianGrid stroke="#eef1f4" />
              <XAxis type="number" dataKey="x" name="Volatility" unit="%" tick={{ fontSize: 11 }} label={{ value: 'Volatility', position: 'insideBottom', offset: -5, fontSize: 11 }} />
              <YAxis type="number" dataKey="y" name="Return" unit="%" tick={{ fontSize: 11 }} width={50} />
              <ZAxis range={[120, 120]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [v.toFixed(2) + '%', n]} labelFormatter={() => ''}
                       content={({ payload }) => payload?.length ? (
                         <div style={{ background: '#fff', border: '1px solid #D9DDE3', padding: '6px 10px', fontSize: 12 }}>
                           <strong>{payload[0].payload.name}</strong><br />vol {payload[0].payload.x.toFixed(2)}% · return {payload[0].payload.y.toFixed(2)}%
                         </div>) : null} />
              <Scatter data={scatter}>
                {scatter.map((d) => <Cell key={d.name} fill={SERIES_COLOUR[d.name] || C.greyMid} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="pf-chart">
          <h3>Month-end asset class exposure (notional, % of NAV)</h3>
          {alloc ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={allocRows} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid stroke="#eef1f4" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis tickFormatter={(v) => v.toFixed(0) + '%'} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={(v) => rangeVal(v).toFixed(1) + '%'} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke={C.navy} />
                {alloc.asset_classes.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={AC_COLOURS[i % AC_COLOURS.length]} />)}
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="pf-muted">Loading…</div>}
        </div>
      </div>
      <div className="pf-foot">Performance as at {perf.as_at} from the monthly APEX NAV series (Plus_Fund_Performance.py, run after each month's valuation). Objective 5% p.a. to Jan 2024, 8% p.a. since.</div>
    </>
  );
}

// ---- APL security master --------------------------------------------------
const APL_TEXT = [
  ['symbol', 'Symbol'], ['security_description', 'Description'], ['bb_ticker', 'BB ticker'],
  ['eikon_label', 'Eikon label'], ['eikon_ticker', 'Eikon ticker'], ['ds_ticker', 'DS ticker'],
  ['currency', 'Ccy'], ['base_currency', 'Base ccy'], ['bb_global_id', 'BBG ID'],
];
const APL_SELECT = [
  ['asset_type', 'Type'], ['asset_class', 'Asset class'], ['sub_asset_class', 'Sub asset class'],
  ['source', 'Source'], ['growth_defensive', 'G / D'], ['fx_exp', 'FX exp'],
];
const APL_NUM = [['duration', 'Duration'], ['delta', 'Delta'], ['strike', 'Strike']];
const NEW_ROW = { security_id: '', symbol: '', security_description: '', asset_type: 'STK', currency: 'AUD', base_currency: 'AUD',
  asset_class: '', sub_asset_class: '', source: 'Market Beta', growth_defensive: 'Growth', fx_exp: 'AUD', ds_ticker: '', bb_ticker: '' };

function APLTab({ apl, unmapped, heldIds, reload }) {
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [heldOnly, setHeldOnly] = useState(false);
  const [saved, setSaved] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(NEW_ROW);
  const [saving, setSaving] = useState(false);

  const save = async (security_id, field, raw) => {
    let value = raw;
    if (APL_NUM.some(([k]) => k === field)) value = raw === '' || raw === null ? null : Number(raw);
    else if (typeof raw === 'string') value = raw.trim() === '' ? null : raw.trim();
    setErr(null);
    try {
      await api.put('/api/plus-fund/apl', { items: [{ security_id, [field]: value }] });
      setSaved(`${security_id} · ${field} saved`);
      setTimeout(() => setSaved(null), 2500);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const deactivate = async (security_id) => {
    setErr(null);
    try { await api.delete(`/api/plus-fund/apl/${encodeURIComponent(security_id)}`); reload(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const addRow = async () => {
    if (!draft.security_id.trim()) { setErr('SecurityID is required'); return; }
    setSaving(true); setErr(null);
    const item = {};
    Object.entries(draft).forEach(([k, v]) => { if (v !== '' && v !== null) item[k] = v; });
    try { await api.put('/api/plus-fund/apl', { items: [item] }); setDraft(NEW_ROW); reload(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  const options = apl?.options || {};
  const opt = (k) => options[k] || [];
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (apl?.rows || []).filter((r) => (showInactive || r.is_active) && (!heldOnly || heldIds.has(r.security_id))
      && (!needle || [r.security_id, r.symbol, r.security_description, r.bb_ticker, r.asset_class].some((v) => (v || '').toLowerCase().includes(needle))));
  }, [apl, q, showInactive, heldOnly, heldIds]);

  return (
    <>
      <div className="pf-note">
        The APL is the security master the 05:04 Plus Fund job reads from this table: asset class, growth/defensive, FX exposure,
        option delta/strike and the SecurityID mapping for futures and options (by BB ticker). Edits save on blur and apply at the
        next morning's run. DS ticker is the Datastream mnemonic for the trailing-return columns (ASX: &lt;IOZ.AX&gt;, US: U:VT);
        new mnemonics are picked up by the 06:23 ticker refresh.
      </div>
      {unmapped?.rows?.length > 0 && (
        <div className="pf-alert">
          <strong>Unmapped in the latest IB file ({unmapped.rows[0].ReportDate}):</strong>{' '}
          {unmapped.rows.map((r) => `${r.Symbol || r.BBTicker || r.SecurityID} (${r.AssetType}, ${pct(r.Weight_Fund, 2) || '0%'})`).join(' · ')}
          . Add them below (SecurityID must match the IB file; for futures/options the BB ticker drives the match).
        </div>
      )}
      <div className="pf-add-row">
        <label>SecurityID *<input value={draft.security_id} onChange={(e) => setDraft({ ...draft, security_id: e.target.value })} placeholder="ISIN / futures code" /></label>
        <label>Symbol<input value={draft.symbol} onChange={(e) => setDraft({ ...draft, symbol: e.target.value })} /></label>
        <label>Description<input value={draft.security_description} onChange={(e) => setDraft({ ...draft, security_description: e.target.value })} /></label>
        <label>BB ticker<input value={draft.bb_ticker} onChange={(e) => setDraft({ ...draft, bb_ticker: e.target.value })} /></label>
        <label>Type<select value={draft.asset_type} onChange={(e) => setDraft({ ...draft, asset_type: e.target.value })}>{['STK', 'FUT', 'OPT', 'CASH', 'INTACC', 'DIVACC'].map((o) => <option key={o}>{o}</option>)}</select></label>
        <label>Asset class<select value={draft.asset_class} onChange={(e) => setDraft({ ...draft, asset_class: e.target.value })}><option value="">–</option>{opt('asset_class').map((o) => <option key={o}>{o}</option>)}</select></label>
        <label>Sub asset class<select value={draft.sub_asset_class} onChange={(e) => setDraft({ ...draft, sub_asset_class: e.target.value })}><option value="">–</option>{opt('sub_asset_class').map((o) => <option key={o}>{o}</option>)}</select></label>
        <label>Source<select value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })}>{opt('source').map((o) => <option key={o}>{o}</option>)}</select></label>
        <label>G / D<select value={draft.growth_defensive} onChange={(e) => setDraft({ ...draft, growth_defensive: e.target.value })}>{opt('growth_defensive').map((o) => <option key={o}>{o}</option>)}</select></label>
        <label>Ccy<input value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} /></label>
        <label>FX exp<input value={draft.fx_exp} onChange={(e) => setDraft({ ...draft, fx_exp: e.target.value })} /></label>
        <label>DS ticker<input value={draft.ds_ticker} onChange={(e) => setDraft({ ...draft, ds_ticker: e.target.value })} placeholder="<XYZ.AX>" /></label>
        <button className="pf-btn" onClick={addRow} disabled={saving}>Add security</button>
      </div>
      <div className="pf-toolbar">
        <input type="text" placeholder="Search ID, symbol, description…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label><input type="checkbox" checked={heldOnly} onChange={(e) => setHeldOnly(e.target.checked)} /> held only</label>
        <label><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> show inactive</label>
        <span className="pf-muted">{rows.length} of {apl?.rows?.length || 0}</span>
        {saved && <span className="pf-saved">{saved}</span>}
      </div>
      {err && <div className="pf-alert">{String(err)}</div>}
      <div className="pf-table-scroll">
        <table className="pf-table pf-apl-table">
          <thead>
            <tr>
              <th className="pf-l">SecurityID</th>
              {APL_TEXT.slice(0, 2).map(([k, l]) => <th key={k} className="pf-l">{l}</th>)}
              {APL_SELECT.map(([k, l]) => <th key={k} className="pf-l">{l}</th>)}
              {APL_NUM.map(([k, l]) => <th key={k}>{l}</th>)}
              {APL_TEXT.slice(2).map(([k, l]) => <th key={k} className="pf-l">{l}</th>)}
              <th className="pf-l">Updated</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.security_id} className={`${r.is_active ? '' : 'pf-inactive'} ${heldIds.has(r.security_id) ? 'pf-held' : ''}`}>
                <td className="pf-l pf-code" title={heldIds.has(r.security_id) ? 'currently held' : ''}>{r.security_id}</td>
                {APL_TEXT.slice(0, 2).map(([k]) => <td key={k}><input defaultValue={r[k] || ''} onBlur={(e) => e.target.value !== (r[k] || '') && save(r.security_id, k, e.target.value)} /></td>)}
                {APL_SELECT.map(([k]) => (
                  <td key={k}>
                    <select defaultValue={r[k] || ''} onChange={(e) => save(r.security_id, k, e.target.value)}>
                      <option value="">–</option>
                      {(opt(k).includes(r[k]) || !r[k] ? opt(k) : [r[k], ...opt(k)]).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                ))}
                {APL_NUM.map(([k]) => <td key={k}><input className="pf-num" defaultValue={isNum(r[k]) ? r[k] : ''} onBlur={(e) => String(e.target.value) !== String(isNum(r[k]) ? r[k] : '') && save(r.security_id, k, e.target.value)} /></td>)}
                {APL_TEXT.slice(2).map(([k]) => <td key={k}><input defaultValue={r[k] || ''} onBlur={(e) => e.target.value !== (r[k] || '') && save(r.security_id, k, e.target.value)} /></td>)}
                <td className="pf-l pf-sub" title={r.updated_at}>{r.updated_by || ''}</td>
                <td>{r.is_active ? <button className="pf-link" onClick={() => deactivate(r.security_id)}>deactivate</button>
                  : <button className="pf-link" onClick={() => save(r.security_id, 'is_active', true).then(reload)}>reactivate</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- App --------------------------------------------------------------------
const TABS = [
  ['front', 'Front page'], ['valuation', 'Valuation'], ['exposure', 'Exposure vs DDP'], ['rebal', 'Rebalancing and drift'], ['sn', 'SN portfolio'],
  ['ticket', 'Trade ticket'], ['orders', 'Trade log'], ['flowcalc', 'Apps and redemptions'], ['performance', 'Performance'],
  ['contribution', 'Contribution'], ['history', 'History'], ['flows', 'Flows'], ['report', 'Monthly report'], ['apl', 'APL security master'],
];

export default function PlusFundApp() {
  const [tab, setTab] = useState('front');
  const meta = useFetch('/api/plus-fund/meta');
  const summary = useFetch('/api/plus-fund/summary');
  const trailing = useFetch('/api/plus-fund/trailing-returns');
  const exposure = useFetch('/api/plus-fund/exposure-vs-ddp', null, tab === 'exposure');
  const rebal = useFetch('/api/plus-fund/rebalancing', null, tab === 'rebal');
  const sn = useFetch('/api/plus-fund/sn-holdings', null, tab === 'sn');
  const perf = useFetch('/api/plus-fund/performance', null, tab === 'performance');
  const alloc = useFetch('/api/plus-fund/allocation-history', null, tab === 'performance');
  const apl = useFetch('/api/plus-fund/apl', null, tab === 'apl' || tab === 'ticket');
  const valuation = useFetch('/api/plus-fund/valuation');
  const currency = useFetch('/api/plus-fund/currency-exposure');
  const unmapped = useFetch('/api/plus-fund/apl/unmapped', null, tab === 'apl');
  const heldIds = useMemo(() => new Set((summary.data?.groups || []).flatMap((g) => g.securities.map((s) => s.SecurityID))), [summary.data]);

  const m = meta.data;
  const stale = m?.latest_holdings && (Date.now() - new Date(m.latest_holdings).getTime()) / 86400000 > 5;
  return (
    <div className="pf-wrap">
      <div className="pf-head">
        <h1>Dynamic Plus Fund</h1>
        {m && (
          <>
            <span className={`pf-pill ${stale ? 'pf-pill-warn' : 'pf-pill-navy'}`}>IB holdings as at {m.latest_holdings || '–'}{stale ? ' · STALE' : ''}</span>
            <span className="pf-pill pf-pill-navy">Performance to {m['Monthly Performance'] || '–'}</span>
            <span className="pf-muted">Last RDS sync {m.last_sync ? new Date(m.last_sync).toLocaleString('en-AU') : '–'}</span>
          </>
        )}
        {meta.error && <span className="pf-pill pf-pill-breach">meta: {String(meta.error)}</span>}
      </div>
      <div className="pf-tabs">
        {TABS.map(([k, l]) => <button key={k} className={`pf-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === 'front' && (<><Status loading={summary.loading} error={summary.error} /><FrontPage summary={summary.data} trailing={trailing.data} trailingError={trailing.error} valuation={valuation.data} currencyExposure={currency.data} /></>)}
      {tab === 'valuation' && <ValuationTab />}
      {tab === 'ticket' && (<><Status loading={summary.loading || apl.loading} error={summary.error || apl.error} /><TicketTab summary={summary.data} valuation={valuation.data} apl={apl.data} /></>)}
      {tab === 'flowcalc' && (<><Status loading={summary.loading} error={summary.error} /><FlowCalcTab summary={summary.data} trailing={trailing.data} /></>)}
      {tab === 'contribution' && <ContributionTab />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'flows' && <FlowsTab />}
      {tab === 'report' && <ReportTab />}
      {tab === 'orders' && <TradeLogTab />}
      {tab === 'exposure' && (<><Status loading={exposure.loading} error={exposure.error} /><ExposureTab data={exposure.data} /></>)}
      {tab === 'rebal' && (<><Status loading={rebal.loading} error={rebal.error} /><RebalTab data={rebal.data} /></>)}
      {tab === 'sn' && (<><Status loading={sn.loading} error={sn.error} /><SNTab data={sn.data} /></>)}
      {tab === 'performance' && (<><Status loading={perf.loading || alloc.loading} error={perf.error || alloc.error} /><PerformanceTab perf={perf.data} alloc={alloc.data} /></>)}
      {tab === 'apl' && (<><Status loading={apl.loading} error={apl.error || unmapped.error} /><APLTab apl={apl.data} unmapped={unmapped.data} heldIds={heldIds} reload={() => { apl.reload(); unmapped.reload(); }} /></>)}
    </div>
  );
}
